import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { hasArrow, isLegacyPinned, MAX_CALLOUT_PCT, staysOnScreen } from "../../../shared/callout";
import type { Callout, Step } from "../../../shared/types";
import { api } from "../../api";
import { trackCardHeight } from "../../calloutHeight";
import { IconX } from "../../icons";
import { setPendingSave, setSaveStatus } from "../dirty";
import { useEditor } from "../DemoEditor";

export const DEFAULT_CALLOUT: Callout = { title: "", body: "", button: "", x: 6, y: 8, positionMode: "pinned", pageCoords: true, showButton: true, showNextButton: true };
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

/**
 * A screen's callout, kept in the editor and saved by itself a moment after each change (the same
 * way Screen settings save). Pending edits are flushed when the screen is left.
 */
export function useCallout(step: Step) {
  const { setBundle } = useEditor();
  const [callout, setCallout] = useState<Callout | null>(step.callout);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const pending = useRef<{ value: Callout | null } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(0);

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    setPendingSave(false);
    const mine = ++inFlight.current;
    try {
      const b = await api.updateStep(step.id, { callout: p.value });
      if (mine === inFlight.current) { setBundle(b); setState("saved"); setError(null); setSaveStatus("saved"); }
    } catch (e: any) {
      if (mine === inFlight.current) { setState("error"); setError(e.message); setSaveStatus("error", e.message); }
    }
  }, [step.id, setBundle]);

  // Leaving the screen (or the studio) must not lose a change made in the last moment.
  useEffect(() => () => { if (pending.current) void flush(); }, [flush]);

  // The callout as last changed, not as of the render a handler was made in: a change made a frame later (attaching
  // after an arrow is picked, or after a drag) must build on the one just made, not undo it.
  const latest = useRef(callout);
  latest.current = callout;
  const commit = (next: Callout | null, now = false) => {
    latest.current = next;
    setCallout(next);
    pending.current = { value: next };
    setPendingSave(true);
    setSaveStatus("dirty");
    if (timer.current) clearTimeout(timer.current);
    if (now) void flush(); else timer.current = setTimeout(() => void flush(), 1500);
  };

  return {
    callout, state, error, flush,
    /** `at`: how far the page is scrolled (% of the screen), so the new callout lands in the part on screen. */
    add: (at?: { x: number; y: number }) => { if (!latest.current) commit({ ...DEFAULT_CALLOUT, x: DEFAULT_CALLOUT.x + (at?.x ?? 0), y: DEFAULT_CALLOUT.y + (at?.y ?? 0) }, true); },
    remove: () => commit(null, true),
    patch: (p: Partial<Callout>) => { if (latest.current) commit({ ...latest.current, ...p }); },
    /**
     * A Moves-with-Page callout saved before page coordinates is measured from the page as it opened: add how far
     * that was scrolled (`at`, % of the screen) to get its spot on the page. Not saved by itself; whatever saves
     * the callout next writes it, and until then the player does the same sum.
     */
    adoptPageCoords: (at: { x: number; y: number }) => setCallout((cur) => (cur && isLegacyPinned(cur)
      ? { ...cur, x: Math.min(MAX_CALLOUT_PCT, cur.x + at.x), y: Math.min(MAX_CALLOUT_PCT, cur.y + at.y), pageCoords: true }
      : cur)),
  };
}
export type CalloutState = ReturnType<typeof useCallout>;

/**
 * The callout as it sits on the canvas: the viewer's card (the same size, fonts and padding, so it covers exactly what
 * the viewer's does), typed into in place. Its editor controls float outside it: the bar above (drag, mode, remove) and
 * the "+ Variable" row below. A field the viewer wouldn't show (an empty title or text) only shows while the card is
 * being edited, so the card is the viewer's size whenever it isn't.
 *
 * A callout that moves with the page sits in a layer the canvas shifts as the page scrolls; `pageShift` is that shift
 * (% of the screen, negative when scrolled down). A drag works out where on the screen the card is, keeps it inside the
 * screen, and saves that spot plus the scroll: its spot on the page. `tip`: an attached callout's arrow tip (% of the
 * page, as x/y are), which the card hangs off. When a callout with an arrow is let go, `onDrop` attaches it to what
 * its arrow points at.
 */
export default function CalloutCard({ co, pageShift, tip, stepLabel, onDrop }: {
  co: CalloutState; pageShift?: { dx: number; dy: number }; tip?: { x: number; y: number } | null; stepLabel: string; onDrop?: () => void;
}) {
  const c = co.callout!;
  const card = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLTextAreaElement>(null);
  const body = useRef<HTMLTextAreaElement>(null);
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => trackCardHeight(card.current), []);

  // The viewer shows a title or text only when it has one. Both empty: both, or there would be nothing to type in.
  const blank = !c.title.trim() && !c.body.trim();
  const showTitle = blank || editing || !!c.title.trim();
  const showBody = blank || editing || !!c.body.trim();

  // Each field is exactly as tall as its text, like the heading and paragraph it becomes.
  useLayoutEffect(() => {
    for (const el of [title.current, body.current]) {
      if (!el) continue;
      el.style.height = "0px";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [c.title, c.body, showTitle, showBody, c.width]);

  // The bar and the row go on the other side of the card when the screen's edge is too close to show them, or when
  // they would cover what the card's arrow points at (a top arrow points up past the bar, a bottom one down past the row).
  useLayoutEffect(() => {
    const el = card.current;
    const s = el?.closest(".editor-stage")?.getBoundingClientRect();
    if (!el || !s) return;
    const r = el.getBoundingClientRect();
    const up = hasArrow(c) && c.arrow === "top", down = hasArrow(c) && c.arrow === "bottom";
    const barBelow = up || r.top - s.top < 34;
    el.classList.toggle("bar-below", barBelow);
    el.classList.toggle("foot-above", !barBelow && (down || s.bottom - r.bottom < 36));
  });

  const stageRect = () => (card.current?.closest(".editor-stage, .stage") as HTMLElement | null)?.getBoundingClientRect() ?? card.current!.offsetParent!.getBoundingClientRect();
  // Dragged by the bar, or by any part of the card that isn't a field or a button.
  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (e.button !== 0 || (e.target as HTMLElement).closest("textarea, input, button, .callout-foot")) return;
    const r = card.current!.getBoundingClientRect();
    drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top, moved: false };
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
  };
  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    const s = stageRect();
    const r = card.current!.getBoundingClientRect();
    // Where on the screen: anywhere the whole card fits (8px clear of the bottom, as the player keeps it). The card is
    // drawn scaled to the screen (src/calloutScale.ts), so those 8px are scaled too.
    const k = r.width / (card.current!.offsetWidth || r.width);
    const maxX = Math.max(0, ((s.width - r.width) / s.width) * 100);
    const maxY = Math.max(0, ((s.height - r.height - 8 * k) / s.height) * 100);
    const onX = clamp(((e.clientX - drag.current.dx - s.left) / s.width) * 100, 0, maxX);
    const onY = clamp(((e.clientY - drag.current.dy - s.top) / s.height) * 100, 0, maxY);
    drag.current.moved = true;
    // Saved: that spot plus however far the page is scrolled (nothing to add for a callout that stays on screen). While
    // it moves it is attached to nothing; let go, it attaches to what its arrow points at (onDrop).
    co.patch({
      x: Math.round(Math.max(0, onX - (pageShift?.dx ?? 0)) * 10) / 10,
      y: Math.round(Math.max(0, onY - (pageShift?.dy ?? 0)) * 10) / 10,
      ...(c.anchor ? { anchor: null } : {}),
    });
  };
  const onUp = () => {
    const moved = drag.current?.moved;
    drag.current = null;
    setDragging(false);
    // Attached once the last move is on screen, from where the card is then.
    if (moved && hasArrow(c) && onDrop) requestAnimationFrame(() => requestAnimationFrame(onDrop));
  };

  const insertVariable = () => {
    const el = body.current;
    const token = "{{company|your team}}";
    if (!el) return co.patch({ body: c.body + token });
    const a = el.selectionStart ?? c.body.length, z = el.selectionEnd ?? a;
    co.patch({ body: c.body.slice(0, a) + token + c.body.slice(z) });
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + token.length, a + token.length); });
  };

  const attached = !!tip && !dragging;
  return (
    <>
      {c.modal && <div className="callout-backdrop" />}
      <div
        ref={card}
        className={`callout callout-edit ${dragging ? "dragging" : ""} ${c.modal ? "callout-modal" : ""} ${attached ? "callout-attached" : staysOnScreen(c) ? "callout-sticky" : "callout-pinned"} ${c.arrow && c.arrow !== "none" ? `callout-arrow-${c.arrow}` : ""} ${c.theme && c.theme !== "default" ? `callout-theme-${c.theme}` : ""}`}
        data-callout
        style={{
          ["--cx" as any]: `${c.x}%`,
          ["--cy" as any]: `${c.y}%`,
          ...(attached ? { ["--tx" as any]: `${tip!.x}%`, ["--ty" as any]: `${tip!.y}%` } : {}),
          ...(c.width ? { ["--cw" as any]: `${c.width}px` } : {}),
        } as React.CSSProperties}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        onFocus={() => setEditing(true)}
        onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setEditing(false); }}
      >
        {c.arrow && c.arrow !== "none" && <span className="callout-pointer" aria-hidden="true" />}
        <div className="callout-bar" title="Drag to move">
          <span className="callout-mode">{c.modal ? "Modal callout" : attached ? "Callout · Attached" : staysOnScreen(c) ? "Callout · Stays on Screen" : "Callout · Moves with Page"}</span>
          <span className="callout-state" role="status">{co.state === "error" ? "Not saved" : ""}</span>
          <button type="button" className="callout-x" onClick={co.remove} aria-label="Remove callout" title="Remove callout"><IconX className="icon icon-xs" /></button>
        </div>
        <span className="callout-step">{stepLabel}</span>
        {showTitle && (
          <textarea ref={title} className="callout-title" value={c.title} maxLength={200} rows={1} placeholder="Title" aria-label="Callout title"
            onChange={(e) => co.patch({ title: e.target.value.replace(/\n/g, " ") })} onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }} />
        )}
        {showBody && (
          <textarea ref={body} className="callout-body" value={c.body} maxLength={600} rows={1} placeholder="What should the viewer know here?" aria-label="Callout text" onChange={(e) => co.patch({ body: e.target.value })} />
        )}
        {c.showButton !== false && c.showNextButton !== false && (
          <input className="callout-btn" value={c.button} maxLength={40} placeholder="Next" aria-label="Button label" onChange={(e) => co.patch({ button: e.target.value })} />
        )}
        <div className="callout-foot">
          <button type="button" className="btn btn-sm btn-ghost" onClick={insertVariable} title="Insert a personalised value, with a fallback">+ Variable</button>
          {(c.showButton === false || c.showNextButton === false) && <span className="small muted" style={{ fontStyle: "italic" }}>No Next button</span>}
          {co.state === "error" && <span className="small text-danger" role="alert">{co.error}</span>}
        </div>
      </div>
    </>
  );
}
